import React, { useState, useEffect, useMemo } from 'react';
import Plot from 'react-plotly.js';
import { ControlPanel, ControlGroup, DataTable, Loader } from '../components/Components';
import './gene.css';

// Searchable Select Component - Same as PathwayPage.js
const SearchableSelect = React.memo(({ options = [], value, onChange, placeholder, label }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // Use useMemo instead of useEffect + state for filtering to avoid extra renders
    const filteredOptions = useMemo(() => {
        if (!searchTerm) return options;
        return options.filter(option =>
            option.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [searchTerm, options]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.searchable-select')) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen]);

    const handleSelect = (option) => {
        onChange(option);
        setIsOpen(false);
        setSearchTerm('');
    };

    return (
        <div className="searchable-select">
<div className="searchable-select-input" onClick={() => setIsOpen(true)}>
                <input
                    type="text"
                    value={isOpen ? searchTerm : value || ''}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={placeholder}
                    onFocus={() => setIsOpen(true)}
                    className="search-input"
                />
                <svg className="dropdown-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </div>

            {isOpen && (
                <div className="searchable-select-dropdown">
                    <div className="dropdown-content">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option, index) => (
                                <div
                                    key={index}
                                    className={`dropdown-option ${option === value ? 'selected' : ''}`}
                                    onClick={() => handleSelect(option)}
                                >
                                    {option}
                                </div>
                            ))
                        ) : (
                            <div className="dropdown-no-results">No results found</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
});

// Clean DataTable component - Same as PathwayPage.js
const CleanDataTable = ({ data, title, isDynamic = false }) => {

    if (!data || data.length === 0) {
        return (
            <div className="gene-glass-card" style={{ height: '100%' }}>
                <div className="gene-card-title">
                    {title}
                    <span className="gene-table-count">0 items</span>
                </div>
                <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}>
                    No data available
                </div>
            </div>
        );
    }
    
    const headers = Object.keys(data[0]);
    // Remove module column, put ID first
    let filteredHeaders = headers.filter(header => header !== 'module');
    if (filteredHeaders.includes('ID')) {
        filteredHeaders = ['ID', ...filteredHeaders.filter(h => h !== 'ID')];
    }
    
    return (
        <div className="gene-glass-card" style={{ height: '100%' }}>
            <div className="gene-card-title">
                {title}
                <span className="gene-table-count">{data.length} items</span>
            </div>
            <div className={`gene-table-wrapper ${isDynamic ? 'gene-table-dynamic' : ''}`} style={{ maxHeight: 'calc(100%)', overflow: 'auto', position: 'relative' }}>
                <table className="gene-enhanced-table">
                    <thead>
                        <tr>
                            {filteredHeaders.map(header => (
                                <th key={header}>
                                    {header.replace(/_/g, ' ').split(' ').map(word =>
                                        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                                    ).join(' ')}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        <tr style={{ height: '2px' }}><td colSpan={filteredHeaders.length} style={{ padding: 0, border: 'none' }}></td></tr>
                        {data.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                                {filteredHeaders.map(header => (
                                    <td
                                        key={header}
                                        className={isDynamic ? '' : 'gene-table-cell'}
                                        style={{
                                            fontWeight: header === 'ID' ? 'bold' : 'normal',
                                            color: header === 'ID' ? '#6b7280' : 'inherit'
                                        }}
                                        title={String(row[header] || '')}
                                    >
                                        {typeof row[header] === 'number' ? (
                                            Number(row[header]).toFixed(4) 
                                        ) : (
                                            String(row[header] || '')
                                        )}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>


            </div>
        </div>
    );
};

function CoExpressionPage({ 
    selections, 
    handleSelection, 
    options, 
    data, 
    isTabLoading,
    diseaseColors 
}) {
    // Simple loading logic
    const shouldShowLoading = isTabLoading;

    // Clean plot configuration
    const plotConfig = {
        displayModeBar: false,
        responsive: true
    };

    const plotLayout = {
        font: { family: '-apple-system, BlinkMacSystemFont, Segoe UI, sans-serif', size: 11 },
        paper_bgcolor: 'rgba(0,0,0,0)',
        plot_bgcolor: 'rgba(0,0,0,0)',
        margin: { l: 50, r: 20, t: 0, b: 170 } // Increased bottom margin for rotated labels
    };

    return (
        <div className="gene-page">
            {/* Sidebar */}
            <div className="gene-sidebar">
                <h1 className="gene-sidebar-title">Co-Expression Analysis</h1>
                
                <div className="gene-sidebar-section">
                    <p className="gene-sidebar-label">Select Module</p>
                    <SearchableSelect
                        options={(options.modules || []).sort()}
                        value={selections.module}
                        onChange={(value) => handleSelection('module', value)}
                        placeholder="Search modules..."
                    />
                </div>
            </div>

            {/* Main Content */}
            <div className="gene-main-content">
                {/* Loading State */}
                {shouldShowLoading && (
                    <div className="gene-loading">
                        <div className="gene-loading-spinner"></div>
                        Loading co-expression data...
                    </div>
                )}

                {/* Content */}
                {!shouldShowLoading && (
                    <div className="gene-content">
                        {/* Column Layout */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                            
                            {/* Module Eigengenes Plot */}
                            <div className="gene-glass-card">
                                <div className="gene-card-title">
                                    Module Eigengenes
                                    <span className="gene-tag">{selections.module}</span>
                                </div>
                                <p className="gene-card-subtitle">
                                    Expression patterns across diseases for the selected module
                                </p>
                                
                                <div 
                                    className="gene-plot-container" 
                                    style={{ height: '500px', overflow: 'hidden' }}
                                >
                                    <Plot 
                                        data={[{
                                            x: data.moduleEigengenes?.map(d => d.disease),
                                            y: data.moduleEigengenes?.map(d => d.value),
                                            type: 'box',
                                            boxpoints: 'all',
                                            jitter: 0.3,
                                            pointpos: 0,
                                            fillcolor: 'rgba(0,0,0,0)',
                                            line: {  width: 1 },
                                            marker: {
                                                color: data.moduleEigengenes?.map(d => diseaseColors[d.disease] || '#cccccc'),
                                                size: 4,
                                                opacity: 0.7
                                            },
                                            hovertemplate: '<b>%{x}</b><br>Value: %{y:.3f}<extra></extra>',
                                            name: ''
                                        }]}
                                        layout={{
                                            ...plotLayout,
                                            height: 500,
                                            showlegend: false,
                                            yaxis: { 
                                                title: {
                                                    text: 'Eigengene Value',
                                                    font: { size: 14, color: '#374151' }
                                                },
                                                titlefont: { size: 14 },
                                                tickfont: { size: 12, color: '#6b7280' },
                                                gridcolor: 'rgba(0,0,0,0.1)',
                                                showgrid: true,
                                                zeroline: true,
                                                zerolinecolor: 'rgba(0,0,0,0.3)',
                                                zerolinewidth: 1
                                            },
                                            xaxis: { 
                                                title: {
                                                    text: 'Disease Type',
                                                    font: { size: 14, color: '#374151' }
                                                },
                                                tickangle: -45, 
                                                titlefont: { size: 14 },
                                                tickfont: { size: 11, color: '#6b7280' },
                                                showgrid: false,
                                                showline: true,
                                                linecolor: 'rgba(0,0,0,0.2)',
                                                linewidth: 1
                                            },
                                            plot_bgcolor: 'rgba(248, 250, 252, 0.5)',
                                            hovermode: 'closest'
                                        }}
                                        config={plotConfig}
                                        useResizeHandler={true}
                                        style={{ width: '100%', height: '100%' }}
                                    />
                                </div>
                            </div>

                            {/* Data Tables Side by Side */}
                            <div style={{ display: 'flex', gap: '20px' }}>
                                {/* Module Genes Table */}
                                <div className="gene-table-narrow">
                                    <CleanDataTable 
                                        data={data.moduleGenes?.map(gene => ({gene}))} 
                                        title="Module Genes" 
                                    />
                                </div>

                                {/* GSOA Enrichment Table */}
                                <div className="gene-table-wide">
                                    <CleanDataTable 
                                        data={data.moduleGsoa} 
                                        title="GSOA Enrichment" 
                                        isDynamic={true}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default CoExpressionPage;
